/**
 * AddAliasModal — the add-alias control (plan §5.2 cta_master_aliases,
 * §13.4 WP-33b frontend scope "an add-alias control"). POST
 * /master/:type/:id/aliases, source 'user'.
 */
import { useState, FormEvent } from 'react';
import { Modal, fieldCls, primaryBtnCls, secondaryBtnCls, useToast } from '../../../../ui';
import { addMasterAlias } from '../../../api/client';
import type { MasterEntry, MasterListType } from '../../../api/client';

interface Props {
  isOpen: boolean;
  listType: MasterListType;
  entry: MasterEntry | null;
  onClose: () => void;
  onAdded: (entry: MasterEntry) => void;
}

export function AddAliasModal({ isOpen, listType, entry, onClose, onAdded }: Props) {
  const { toast } = useToast();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function handleClose() {
    setText('');
    setError(null);
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!entry) return;
    if (!text.trim()) {
      setError('An alias is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const alias = await addMasterAlias(listType, entry.id, text.trim());
      toast(`Alias added to ${entry.official_name}.`, { type: 'success' });
      onAdded({ ...entry, aliases: [...entry.aliases, alias] });
      handleClose();
    } catch (err) {
      const res = (err as { response?: { data?: any } })?.response;
      setError(typeof res?.data?.error === 'string' ? res.data.error : 'Something went wrong adding the alias.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!entry) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Add alias — ${entry.official_name}`} maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-alias-form">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}
        <div>
          <label htmlFor="cta-alias-text" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Alternate spelling
          </label>
          <input
            id="cta-alias-text"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className={`${fieldCls} w-full`}
            autoFocus
            data-testid="add-alias-text-input"
          />
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={handleClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} className={primaryBtnCls}>
            {submitting ? 'Adding…' : 'Add alias'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AddAliasModal;
